import React, {useEffect, useMemo, useState} from "react";
import {Box, Button, Collapse, IconButton, LinearProgress, Stack, Typography} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import {DocumentList, EmpDocumentRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {dateFromToString} from "../../../components/utils/dates";
import {fileLocalSave} from "../../../components/form/form_inputs/form_file";
import {
	btnSx,
	EmptyState,
	Page,
	PageHeader,
	Panel,
	SearchField,
	SkeletonRows,
	SoftChip,
} from "../../../components/kit";

interface Group {
	key: string;
	name: string;
	position: number;
	docs: DocumentList[];
}

const NO_TOPIC = "Otros";

// Sin acentos ni mayúsculas, para que buscar «politica» encuentre «Política».
const normalize = (s?: string) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Por tema, en el orden del tema y alfabético dentro (#13729). Todo en una página: que ninguno
// quede escondido por haber muchos.
const groupByTopic = (docs: DocumentList[]): Group[] => {
	const groups = new Map<string, Group>();
	docs.forEach(doc => {
		const key = doc.topic?.id ? String(doc.topic.id) : "-";
		if (!groups.has(key)) {
			groups.set(key, {
				key,
				name: doc.topic?.name || NO_TOPIC,
				position: doc.topic?.id ? (doc.topic.position ?? Number.MAX_SAFE_INTEGER - 1) : Number.MAX_SAFE_INTEGER,
				docs: [],
			});
		}
		groups.get(key)!.docs.push(doc);
	});
	const list = Array.from(groups.values());
	list.forEach(g => g.docs.sort((a, b) => a.title.localeCompare(b.title, "es")));
	return list.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "es"));
};

/**
 * Documentos de empresa para el empleado, por tema: se descargan al pulsar y cada uno lleva su acuse
 * «He leído esta edición» (#13676), que pide la ISO 9001, la 27001 y el ENS.
 */
export const EmployeeDocumentListPage = () => {
	const apiManager = useApiManager();
	const documentApi = apiManager.factory(EmpDocumentRestControllerApiFactory);

	const [documents, setDocuments] = useState<DocumentList[]>();
	// Documentos cuya edición vigente ya ha leído (#13676): acuse para ISO 9001, 27001 y ENS.
	const [read, setRead] = useState<Set<number>>(new Set());
	const [term, setTerm] = useState("");
	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

	const loadRead = () =>
		apiManager.executeRaw(documentApi.readEmployeeDocuments()).then((resp) => setRead(new Set(resp.data || [])));

	useEffect(() => {
		apiManager.executeRaw(documentApi.listEmployeeDocuments()).then((resp) => setDocuments(resp.data));
		loadRead();
	}, []);

	const groups = useMemo(() => {
		const t = normalize(term.trim());
		return groupByTopic((documents || []).filter(d => !t || normalize(d.title).includes(t)));
	}, [documents, term]);

	const toggle = (key: string) => setCollapsed(prev => {
		const next = new Set(prev);
		if (next.has(key)) next.delete(key); else next.add(key);
		return next;
	});

	const total = documents?.length || 0;
	const readCount = (documents || []).filter(d => d.id && read.has(d.id)).length;

	const open = (doc: DocumentList) => {
		if (!doc.id) throw new Error("No document id");
		documentApi.viewEmployeeDocument(doc.id).then((resp) => fileLocalSave(resp));
	};

	const action = (doc: DocumentList) => doc.id && (read.has(doc.id)
		? <SoftChip tone="success" label="Leído" icon={<CheckCircleRoundedIcon fontSize="small"/>}/>
		: <Button size="small" variant="outlined" sx={{...btnSx, whiteSpace: "nowrap"}}
				  onClick={() => apiManager.executeRaw(documentApi.markEmployeeDocumentRead(doc.id as number))
					  .then(() => loadRead())}>
			He leído esta edición
		</Button>);

	// La acción va al lado en escritorio y debajo en móvil: al lado se montaba encima del título a 390 px.
	const row = (doc: DocumentList) => (
		<Stack key={doc.id} direction={{xs: "column", sm: "row"}} spacing={{xs: 1, sm: 2}} alignItems={{sm: "center"}}
			   sx={{py: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": {borderBottom: 0}}}>
			<Stack direction="row" spacing={1.5} alignItems="center" onClick={() => open(doc)} role="link" tabIndex={0}
				   onKeyDown={e => e.key === "Enter" && open(doc)}
				   sx={{flexGrow: 1, minWidth: 0, cursor: "pointer", "&:hover .doc-title": {color: "primary.main"}}}>
				<DescriptionOutlinedIcon color="action" fontSize="small"/>
				<Box sx={{minWidth: 0}}>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography className="doc-title" variant="body2" fontWeight={600}>{doc.title}</Typography>
						{/* Caducado no se oculta: se avisa de que está en revisión (#13729). */}
						{doc.reviewStatus === "CADUCADO" && <SoftChip tone="warning" label="En revisión"/>}
					</Stack>
					<Typography variant="caption" color="text.secondary">
						Edición {doc.editionNumber || 1} · {dateFromToString(doc.editionDate || doc.created)}
					</Typography>
				</Box>
			</Stack>
			<Box sx={{flexShrink: 0, pl: {xs: 4, sm: 0}}}>{action(doc)}</Box>
		</Stack>
	);

	return (
		<Page maxWidth={1000}>
			<PageHeader title="Documentos de empresa"
						subtitle="Pulsa un documento para descargarlo. Cuando lo hayas leído, márcalo: es el acuse de la edición vigente."/>

			{documents && total > 0 && (
				<Panel sx={{mb: 3}}>
					<Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}}>
						<Box sx={{flexGrow: 1}}>
							<Typography variant="body2" fontWeight={600}>
								{readCount === total ? "Has leído todos los documentos" : `Has leído ${readCount} de ${total}`}
							</Typography>
							<LinearProgress variant="determinate" value={total ? readCount * 100 / total : 0}
											color={readCount === total ? "success" : "primary"}
											sx={{mt: 1, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
						</Box>
						<SearchField value={term} onChange={setTerm} placeholder="Buscar por título"/>
					</Stack>
				</Panel>
			)}

			{!documents ? <SkeletonRows rows={6}/> : groups.length === 0 ? (
				<Panel>
					<EmptyState icon={<FolderOpenRoundedIcon/>}
								title={term.trim() ? "Ningún documento coincide con la búsqueda." : "Aún no hay documentos."}/>
				</Panel>
			) : (
				<Stack spacing={2}>
					{groups.map(g => {
						const expanded = term.trim() !== "" || !collapsed.has(g.key);
						return (
							<Panel key={g.key}
								   title={<>{g.name} <Typography component="span" variant="body2" color="text.secondary">({g.docs.length})</Typography></>}
								   action={
									   <IconButton size="small" onClick={() => toggle(g.key)} aria-label={expanded ? "Plegar" : "Desplegar"}
												   sx={{transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s"}}>
										   <ExpandMoreRoundedIcon/>
									   </IconButton>
								   }
								   sx={{pb: expanded ? undefined : {xs: 0.5, sm: 1}}}>
								<Collapse in={expanded}>{g.docs.map(row)}</Collapse>
							</Panel>
						);
					})}
				</Stack>
			)}
		</Page>
	);
};
